import { useCallback, useState } from 'react'
import type { ZodError } from 'zod'

import { ErroApi } from '../api/erros'

export type ErrosCampo = Record<string, string>

/**
 * Contrato mínimo de um schema, satisfeito por qualquer `ZodType`.
 *
 * Estrutural, e não `ZodType<Saida>`, porque os schemas de `@baiocchi/shared`
 * têm **entrada diferente da saída** — `zInstante` transforma string em `Date`,
 * `percepcao` tem `.default()` — e `ZodType<T>` fixa entrada e saída no mesmo
 * tipo. Amarrar isso obrigaria um `as never` em cada chamada, que é exatamente o
 * tipo de escape que esconde erro real.
 */
export interface ValidadorSchema<Saida> {
  safeParse: (
    valor: unknown,
  ) => { success: true; data: Saida } | { success: false; error: ZodError }
}

/**
 * Formulário controlado com validação pelos schemas de `@baiocchi/shared`.
 *
 * Sem biblioteca de formulário: os schemas Zod que a API usa para validar são os
 * mesmos que os formulários importam (`leads/schemas.ts`, cabeçalho), então o que
 * falta é pouco — estado, erros por campo e submit. Uma camada de resolvers em
 * cima disso só acrescentaria uma tradução entre o schema e o erro.
 *
 * **A validação do cliente é conveniência; a garantia é a do servidor** (RN-11).
 * Por isso `aplicarErroApi` existe: `422` com `detalhes[]` volta para os campos, e
 * a resposta lista todos os campos faltantes de uma vez (RNF-115) — o atendente
 * corrige tudo em um submit, em vez de descobrir um campo por tentativa.
 */
export function useFormulario<Valores extends object>(inicial: Valores) {
  const [valores, setValores] = useState<Valores>(inicial)
  const [erros, setErros] = useState<ErrosCampo>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  /**
   * O último `ErroApi` recebido, inteiro. As telas precisam do **código** e não
   * só do texto: `CLIENTE_COM_ATENDIMENTO_EM_ANDAMENTO` abre o fluxo de
   * duplicata, `USUARIO_POSSUI_LEADS_EM_ABERTO` abre a escolha de destino e
   * `CONTA_BLOQUEADA` muda o tom do aviso. Decidir isso comparando trechos da
   * mensagem quebraria na primeira revisão de texto do servidor.
   */
  const [erroApi, setErroApi] = useState<ErroApi | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [tocado, setTocado] = useState<Record<string, boolean>>({})

  const definir = useCallback(<Chave extends keyof Valores>(chave: Chave, valor: Valores[Chave]) => {
    setValores((atuais) => ({ ...atuais, [chave]: valor }))
    setErros((atuais) => {
      if (!(chave in atuais)) return atuais
      const proximos = { ...atuais }
      delete proximos[chave as string]
      return proximos
    })
  }, [])

  const definirVarios = useCallback((parcial: Partial<Valores>) => {
    setValores((atuais) => ({ ...atuais, ...parcial }))
  }, [])

  const marcarTocado = useCallback((chave: keyof Valores) => {
    setTocado((atuais) => ({ ...atuais, [chave as string]: true }))
  }, [])

  const reiniciar = useCallback(
    (novos?: Valores) => {
      setValores(novos ?? inicial)
      setErros({})
      setErroGeral(null)
      setErroApi(null)
      setTocado({})
    },
    [inicial],
  )

  /**
   * Traduz `ZodError` e `ErroApi` para erros por campo.
   *
   * O caminho do Zod (`['cliente','contatoPrincipal','numero']`) é achatado com
   * ponto — o mesmo formato que a API usa em `detalhes[].campo`, para que os dois
   * lados caiam no mesmo lugar do formulário.
   */
  const aplicarErroApi = useCallback((erro: unknown) => {
    if (erro instanceof ErroApi) {
      const porCampo = erro.errosPorCampo()
      const temCampo = Object.keys(porCampo).length > 0
      setErroApi(erro)
      setErros(porCampo)
      // Mesmo com erros por campo, a mensagem geral fica: ela é a que explica a
      // regra (`LEAD_TRANSICAO_INVALIDA` diz quais destinos são válidos).
      setErroGeral(erro.message)
      return temCampo
    }
    setErroApi(null)
    setErroGeral(erro instanceof Error ? erro.message : 'Não foi possível concluir a operação.')
    return false
  }, [])

  /**
   * Valida com o schema e chama `aoEnviar`. Devolve `true` quando concluiu.
   *
   * `schema` pode receber uma transformação do estado do formulário para o corpo
   * da API — é o caso da TELA-03, em que o estado é plano (para os campos) e o
   * corpo é aninhado (`{ cliente, lead, observacao }`).
   */
  const enviar = useCallback(
    async <Saida>(
      schema: ValidadorSchema<Saida> | null,
      montarCorpo: (valores: Valores) => unknown,
      aoEnviar: (corpo: Saida) => Promise<void>,
    ): Promise<boolean> => {
      setErroGeral(null)
      setErros({})
      setErroApi(null)

      const bruto = montarCorpo(valores)
      let corpo = bruto as Saida

      if (schema) {
        const resultado = schema.safeParse(bruto)
        if (!resultado.success) {
          const porCampo: ErrosCampo = {}
          for (const problema of resultado.error.issues) {
            const campo = problema.path.join('.')
            porCampo[campo] ??= problema.message
          }
          setErros(porCampo)
          setErroGeral('Confira os campos destacados.')
          return false
        }
        corpo = resultado.data
      }

      setEnviando(true)
      try {
        await aoEnviar(corpo)
        return true
      } catch (erro) {
        aplicarErroApi(erro)
        return false
      } finally {
        setEnviando(false)
      }
    },
    [valores, aplicarErroApi],
  )

  return {
    valores,
    erros,
    erroGeral,
    erroApi,
    enviando,
    tocado,
    definir,
    definirVarios,
    marcarTocado,
    reiniciar,
    enviar,
    aplicarErroApi,
    setErroGeral,
    setErros,
  }
}
